import { isSportsnewFixtureDetailGenerationCurrent } from '../_helpers/sportsnewFixtureDetailLifecycle';
import { sportsnewActiveFixtureMode } from '../_helpers/sportsnewFixturePatchEnvelope';
import { useCallback } from 'react';
import {
  fxId, mergeOddPreservingIdentity, applySportsnewOddPatchesToMarkets,
  mapSportsnewFixtureOdds,
} from '../_helpers/sportsnewListPatch';
import {
  applySportsnewDetailRealtimeSince, sportsnewRealtimeOddsFromFixturePatch,
  recordSportsnewRestOdds, hasSportsnewCommandEvidence,
  snapshotSportsnewReturnedMarketLocks, applySportsnewReturnedMarketLocks,
} from '../_helpers/sportsnewDetailRealtime';
import { selectedPatchFreshness } from '../_helpers/sportsnewSelectedRecovery';
import { sportsnewSelectedCartPatch } from '../_helpers/sportsnewSelectedCartPatch';
import { recoverFixtureSnapshotPayload } from '../_helpers/sportsnewContextConfig';

/**
 * Owns bounded/coalesced fixture REST recovery and its commit decision.
 * Provider-owned refs retain account, socket, detail/list generation and abort authority.
 * The returned callback keeps the original dependency lifetime and follow-up budget.
 */
export const useSportsnewFixtureRecovery = ({
  frameDeliveryRef,
  SportsnewService, modeRef, displayScopeRef, sessionOwnerRef, socketGenerationRef,
  detailRequestGenerationRef, listFilterKeyRef, fixturesRequestSeqRef,
  fixtureRecoveryInFlightRef, detailFixtureIdRef, fixturesRef, selectionsRef,
  socketRef, detailRealtimeLedgerRef, setDetailFixture, reconcileRestDetailMarkets,
  detailMarketsRef, detailAbortControllerRef, latestDetailRequestRef,
  commitDetailMarkets, setDetailLoading, commitFixtures, detailCacheRef,
  updateOddsInCart, selectedFreshnessRef, membershipFailClosedFixturesRef,
  failCloseCartSelectionsForFixtures,
}) => {
  /* One bounded recovery request per fixture at a time. Reconnect, sequence
     gap, and unplaceable-new-odd signals can overlap; coalescing them here
     prevents duplicate authoritative REST snapshots. */
  const recoverFixtureDetailSingleFlight = useCallback((fixtureId, {
    since,
    snapshot = true,
    focusOddStateIds = [],
    followupBudget = 1,
  } = {}) => {
    if (!SportsnewService || fixtureId == null) return Promise.resolve(null);
    const targetFixtureId = String(fixtureId);
    const targetIntentMode = modeRef.current;
    const targetMode = sportsnewActiveFixtureMode(targetIntentMode);
    const targetScope = displayScopeRef.current;
    const recoveryKey = `${targetFixtureId}:${targetMode}:${targetScope}`;
    const owner = {
      session: sessionOwnerRef.current,
      socketGeneration: socketGenerationRef.current,
      detailGeneration: detailRequestGenerationRef.current,
      listKey: listFilterKeyRef.current,
      listGeneration: fixturesRequestSeqRef.current,
    };
    const existing = fixtureRecoveryInFlightRef.current.get(recoveryKey);
    const sameOwner=existing && existing.owner.session === owner.session
      && existing.owner.socketGeneration === owner.socketGeneration
      && existing.owner.detailGeneration === owner.detailGeneration
      && existing.owner.listKey === owner.listKey
      && existing.owner.listGeneration === owner.listGeneration;
    if (sameOwner && !existing.pending) {
      focusOddStateIds.forEach(id=>{if(existing.focus.size<1000)existing.focus.add(String(id));});
      return existing.promise;
    }
    const hasCurrentConsumer = () => String(detailFixtureIdRef.current) === targetFixtureId
      || fixturesRef.current.some(fixture => fxId(fixture) === targetFixtureId)
      || selectionsRef.current.some(selection => String(selection.match_id
        ?? selection.matchId ?? selection.fixtureId ?? selection.fixture_id) === targetFixtureId);
    const canTransferFocus = existing && existing.owner.session === owner.session
      && hasCurrentConsumer();
    // Transfer only unfinished IDs, never an old promise/result or generation.
    // The map key fixes fixture/mode/scope; session and current consumers own intent.
    if ((sameOwner && existing.pending) || canTransferFocus)
      focusOddStateIds = [...existing.focus].filter(id => !existing.served.has(id)).concat(focusOddStateIds);
    existing?.controller?.abort();
    const controller = new AbortController();
    const isIntentCurrent = () => sessionOwnerRef.current === owner.session
      && modeRef.current === targetIntentMode && displayScopeRef.current === targetScope
      && hasCurrentConsumer();
    const mayRead = () => isIntentCurrent() && socketRef.current?.connected !== false
      && socketGenerationRef.current === owner.socketGeneration;
    const flight = { owner, controller, fixtureId:targetFixtureId, isIntentCurrent,
      promise: null, focus:new Set(focusOddStateIds.slice(0,1000).map(String)),served:new Set(),completed:false };

    const fetchSnapshot = async () => {
      for (let attempt = 0; attempt < 3 && !controller.signal.aborted; attempt++) {
        if (!mayRead()) return null;
        const requestedFocus=[...flight.focus].slice(0,50);
        flight.requestedFocus=requestedFocus;
        frameDeliveryRef?.current?.flush();
        const requestRevision = detailRealtimeLedgerRef.current.revision;
        const response = await SportsnewService.recoverFixture(fixtureId, {
      mode: targetMode,
      scope: targetScope,
      snapshot,
      ...(since != null ? { since } : {}),
      ...(requestedFocus.length ? { focus_odd_state_ids: requestedFocus.join(',') } : {}),
        }, { signal: controller.signal, timeout: 5000 }).catch(error => {
          if (controller.signal.aborted) return null;
          return { status: false, httpStatus: Number(error?.response?.status) || 503 };
        });
        if (!mayRead()) return null;
        const retryable=response?.status === false && [429,503,504].includes(Number(response?.httpStatus));
        if (!retryable||attempt===2) return { response, requestRevision };
        if (attempt < 2) await new Promise(resolve => {
          const finish = () => { clearTimeout(timer); controller.signal.removeEventListener('abort', finish); resolve(); };
          const timer = setTimeout(finish, 1000 * (attempt + 1));
          controller.signal.addEventListener('abort', finish, { once: true });
          if (controller.signal.aborted) finish();
        });
      }
      return null;
    };
    const request = fetchSnapshot().then((attemptResult) => {
    frameDeliveryRef?.current?.flush();
      const res = attemptResult?.response;
      const realtimeRevisionAtRequest = attemptResult?.requestRevision;
      const selected = isSportsnewFixtureDetailGenerationCurrent({
        fixtureId: targetFixtureId, generation: owner.detailGeneration, detailFixtureIdRef,
        detailLifecycleGenerationRef: detailRequestGenerationRef,
      });
      const listCurrent = listFilterKeyRef.current === owner.listKey
        && fixturesRequestSeqRef.current === owner.listGeneration
        && fixturesRef.current.some(fixture => fxId(fixture) === targetFixtureId);
      const cartCurrent = selectionsRef.current.some(selection => String(selection.match_id
        ?? selection.matchId ?? selection.fixtureId ?? selection.fixture_id) === targetFixtureId);
      if (
        (!selected && !listCurrent && !cartCurrent)
        || modeRef.current !== targetIntentMode
        || displayScopeRef.current !== targetScope
        || sessionOwnerRef.current !== owner.session
        || socketGenerationRef.current !== owner.socketGeneration
        || fixtureRecoveryInFlightRef.current.get(recoveryKey) !== flight
        || controller.signal.aborted
      ) {flight.obsolete=true;return null;}
      const recovered = recoverFixtureSnapshotPayload(res?.data);
      if (res?.status === false || !recovered.fixture
        || String(fxId(recovered.fixture)) !== targetFixtureId) return null;
      if (selected && recovered.fixture) setDetailFixture(recovered.fixture);
      if (Array.isArray(recovered.markets)) {
        flight.completed = true;
        const requestedFocus = new Set(flight.requestedFocus);
        const rawFocused = recovered.authority === 'writer' ? recovered.focusedOdds.filter(odd => {
          const id = String(odd.oddStateId ?? odd.id);
          const known = detailRealtimeLedgerRef.current.byOddId.get(id)?.patch;
          return requestedFocus.has(id)
            && (odd.fixtureId == null || String(odd.fixtureId) === targetFixtureId)
            && (known?.marketStateId == null || odd.marketStateId == null
              || String(known.marketStateId) === String(odd.marketStateId));
        }) : [];
        // Focused reads follow the market page and may contain newer fields.
        // Combine only raw response rows with the same precedence as display;
        // otherwise the first row's Writer commit would fence its own successor.
        const returnedById = new Map();
        for (const odd of [...sportsnewRealtimeOddsFromFixturePatch({ markets: recovered.markets }), ...rawFocused]) {
          const id = String(odd.oddStateId ?? odd.id);
          returnedById.set(id, mergeOddPreservingIdentity(returnedById.get(id), odd));
        }
        const returnedOdds = [...returnedById.values()];
        const returnedIds = new Set(returnedOdds.map(odd => String(odd.oddStateId ?? odd.id)));
        let reconciled = selected ? reconcileRestDetailMarkets(
          recovered.markets,
          recovered.fixture,
          realtimeRevisionAtRequest,
          recovered.marketsPage,
          {authority:recovered.authority},
        ) : applySportsnewDetailRealtimeSince(recovered.markets, detailRealtimeLedgerRef.current,
          realtimeRevisionAtRequest, [], { authority: recovered.authority, requireWriterForUnlock: targetMode === 'live' });
        const focused = recovered.authority==='writer' ? sportsnewRealtimeOddsFromFixturePatch({markets:
          applySportsnewDetailRealtimeSince(rawFocused
            .map(odd=>({marketStateId:odd.marketStateId,odds:[odd]})),detailRealtimeLedgerRef.current,
          realtimeRevisionAtRequest,detailMarketsRef.current,{authority:recovered.authority})}) : [];
        if(focused.length)reconciled=applySportsnewOddPatchesToMarkets(reconciled,focused);
        // Reconciled includes local off-page rows and overlaid field groups.
        // Those were not returned by Writer and must never clear command evidence.
        const observedOdds = recordSportsnewRestOdds(detailRealtimeLedgerRef.current, returnedOdds,
          realtimeRevisionAtRequest, { authority: recovered.authority });
        // A returned row is not completion when a command newer than this
        // HTTP attempt still owns it. Keep the existing bounded follow-up.
        flight.served = new Set(flight.requestedFocus.filter(id => returnedIds.has(id)
          && !hasSportsnewCommandEvidence(detailRealtimeLedgerRef.current, id)));
        if (selected) {
          // An earlier ordinary detail GET must not overwrite this newer
          // recovery commit. Its owner is invalidated without aborting this flight.
          detailAbortControllerRef.current?.abort();
          latestDetailRequestRef.current = null;
          commitDetailMarkets(reconciled);
          setDetailLoading(false);
        }
        // Reader recovery may converge PREMATCH, but the shared verdict above
        // retains LIVE locks and command evidence until an actual Writer read.
        {
          const byId = new Map([...sportsnewRealtimeOddsFromFixturePatch({ markets: reconciled }),...focused]
            .map(odd => [String(odd.oddStateId ?? odd.id), odd]));
          if (listCurrent) commitFixtures(rows => rows.map(fixture => fxId(fixture) !== targetFixtureId ? fixture
            : mapSportsnewFixtureOdds(fixture, odd => {
              const recoveredOdd = byId.get(String(odd.oddStateId ?? odd.id));
              return recoveredOdd ? mergeOddPreservingIdentity(odd, recoveredOdd) : odd;
            })));
          else if (recovered.authority === 'writer' && listFilterKeyRef.current === owner.listKey) {
            // A newer list request owns membership/price/OPEN. Preserve only
            // the returned selection's effective parent LOCK; session/mode/
            // socket/consumer guards above still fence this response.
            const lockSnapshot = snapshotSportsnewReturnedMarketLocks(detailRealtimeLedgerRef.current, returnedIds);
            commitFixtures(rows => applySportsnewReturnedMarketLocks(rows,
              lockSnapshot, returnedIds, targetFixtureId));
          }
          detailCacheRef.current.delete(recoveryKey);
          byId.forEach(odd => {
            const id = odd.oddStateId ?? odd.id;
            updateOddsInCart?.(id, odd.priceDecimal ?? odd.decimalOdds, sportsnewSelectedCartPatch(odd));
            const observed = observedOdds.get(String(id));
            if (recovered.authority === 'writer' && observed && !hasSportsnewCommandEvidence(detailRealtimeLedgerRef.current, id)) {
              selectedFreshnessRef.current.set(Number(id), selectedPatchFreshness(observed, null));
            }
          });
          if (membershipFailClosedFixturesRef.current.has(targetFixtureId)) failCloseCartSelectionsForFixtures([targetFixtureId]);
        }
      }
      return recovered;
    }).catch(() => null).finally(() => {
      if (fixtureRecoveryInFlightRef.current.get(recoveryKey) === flight) {
        fixtureRecoveryInFlightRef.current.delete(recoveryKey);
        const remaining=[...flight.focus].filter(id=>!flight.served.has(id));
        if(remaining.length&&isIntentCurrent()&&!controller.signal.aborted){
          if(flight.completed&&!flight.obsolete&&mayRead()&&followupBudget>0)return recoverFixtureDetailSingleFlight(fixtureId,
            {snapshot:true,focusOddStateIds:remaining,followupBudget:followupBudget-1});
          // A stale response is still rejected above. Only unfinished IDs remain
          // pending during disconnect; reconnect creates a new request owner.
          flight.focus=new Set(remaining);flight.pending=true;flight.promise=null;
          fixtureRecoveryInFlightRef.current.set(recoveryKey,flight);
        }
      }
    });
    flight.promise = request;
    fixtureRecoveryInFlightRef.current.set(recoveryKey, flight);
    return request;
  }, [
    SportsnewService, modeRef, displayScopeRef, sessionOwnerRef, socketGenerationRef,
    detailRequestGenerationRef, listFilterKeyRef, fixturesRequestSeqRef, fixtureRecoveryInFlightRef,
    detailFixtureIdRef, fixturesRef, selectionsRef, socketRef, detailRealtimeLedgerRef,
    setDetailFixture, reconcileRestDetailMarkets, detailMarketsRef, detailAbortControllerRef,
    latestDetailRequestRef, commitDetailMarkets, setDetailLoading, commitFixtures, detailCacheRef,
    updateOddsInCart, selectedFreshnessRef, membershipFailClosedFixturesRef,
    failCloseCartSelectionsForFixtures,
  ]);

  return recoverFixtureDetailSingleFlight;
};
